import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { env } from '@/config/env';
import { oidcService } from '@/services/oidc';
import type { SignalREventHandler, SignalREventName, SignalRUnsubscribe } from '@/types/SignalR';

class SignalRService {
  private connection: HubConnection | null = null;
  private starting: Promise<void> | null = null;

  private build(): HubConnection {
    return new HubConnectionBuilder()
      .withUrl(env.VITE_SIGNALR_URL, {
        accessTokenFactory: async () => (await oidcService.getAccessToken()) ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();
  }

  async start(): Promise<void> {
    if (this.connection && this.connection.state === HubConnectionState.Connected) return;
    if (this.starting) return this.starting;

    this.connection ??= this.build();
    this.starting = this.connection.start().catch((err) => {
      console.error('[SignalR] start failed', err);
      throw err;
    });

    try {
      await this.starting;
    } finally {
      this.starting = null;
    }
  }

  async stop(): Promise<void> {
    if (!this.connection) return;
    await this.connection.stop();
    this.connection = null;
  }

  subscribe<TPayload>(
    eventName: SignalREventName,
    handler: SignalREventHandler<TPayload>,
  ): SignalRUnsubscribe {
    this.connection ??= this.build();
    this.connection.on(eventName, handler);
    return () => this.connection?.off(eventName, handler);
  }
}

export const signalRService = new SignalRService();
